import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/shared/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  History,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Square,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import {
  getAllScrapeJobs,
  type ScrapeJobProgress,
} from '@/services/scrapeProducts';
import { formatDistanceToNow } from 'date-fns';

interface ScrapeHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScrapeHistoryModal({
  open,
  onOpenChange,
}: ScrapeHistoryModalProps) {
  const [page, setPage] = useState(1);
  const limit = 10;
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['scrapeJobsHistory', page],
    queryFn: () => getAllScrapeJobs(page, limit),
    enabled: open,
    staleTime: 30000, // 30 seconds
  });

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return new Date(dateString).toLocaleString();
    }
  };

  const getStatusBadge = (job: ScrapeJobProgress) => {
    if (job.isStopped) {
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
          <Square className="h-3 w-3 mr-1" />
          Stopped
        </Badge>
      );
    }

    switch (job.status) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            In Progress
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            {job.status}
          </Badge>
        );
    }
  };

  const getProgressPercentage = (job: ScrapeJobProgress): number => {
    if (!job.totalCount || job.totalCount === 0) return 0;
    return Math.round((job.completedCount / job.totalCount) * 100);
  };

  const getSuccessCount = (job: ScrapeJobProgress): number => {
    return job.links?.filter((link) => link.isSuccess).length || 0;
  };

  const getFailedCount = (job: ScrapeJobProgress): number => {
    return job.links?.filter((link) => !link.isSuccess).length || 0;
  };

  const toggleExpand = (jobId: number) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="py-3">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5 text-primary" />
            Scraping History
          </DialogTitle>
          <DialogDescription>
            View all your previous scraping jobs and their results
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="space-y-3 flex-1 overflow-y-auto">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <p className="text-destructive font-medium">
                Failed to load scraping history
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : data && data.data.length > 0 ? (
            <div className="flex-1 overflow-y-auto pr-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">ID</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Results</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((job) => (
                    <>
                      <TableRow key={job.id} className="hover:bg-muted/50">
                        <TableCell className="font-medium">{job.id}</TableCell>
                        <TableCell>
                          <div className="flex items-start gap-2 max-w-md">
                            <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <a
                              href={job.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline break-all break-words"
                              title={job.url}
                            >
                              {job.url}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(job)}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">
                                {job.completedCount} / {job.totalCount}
                              </span>
                              <span className="font-medium">
                                {getProgressPercentage(job)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-primary h-1.5 rounded-full transition-all"
                                style={{
                                  width: `${getProgressPercentage(job)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3 text-sm">
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>{getSuccessCount(job)}</span>
                            </div>
                            {getFailedCount(job) > 0 && (
                              <div className="flex items-center gap-1 text-red-600">
                                <XCircle className="h-3 w-3" />
                                <span>{getFailedCount(job)}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(job.createdAt)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpand(job.id)}
                            className="h-8"
                          >
                            {expandedJobId === job.id ? 'Hide' : 'Details'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expandedJobId === job.id && job.links && (
                        <TableRow>
                          <TableCell colSpan={7} className="p-0">
                            <div className="bg-muted/30 p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm">
                                  Scraped Links ({job.links.length})
                                </h4>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>
                                    Created:{' '}
                                    {new Date(job.createdAt).toLocaleString()}
                                  </span>
                                  {job.finishedAt && (
                                    <span>
                                      Finished:{' '}
                                      {new Date(
                                        job.finishedAt
                                      ).toLocaleString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="max-h-64 overflow-y-auto space-y-2">
                                {job.links.map((link, index) => (
                                  <div
                                    key={index}
                                    className={`p-2 rounded border text-sm ${
                                      link.isSuccess
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-red-50 border-red-200'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <a
                                          href={link.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:underline break-all break-words block"
                                          title={link.link}
                                        >
                                          {link.link}
                                        </a>
                                        {link.error && (
                                          <p className="text-xs text-red-600 mt-1">
                                            {link.error}
                                          </p>
                                        )}
                                        {link.productId && (
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Product ID: {link.productId}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex-shrink-0">
                                        {link.isSuccess ? (
                                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <XCircle className="h-4 w-4 text-red-600" />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <History className="h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground font-medium">
                No scraping history yet
              </p>
              <p className="text-sm text-muted-foreground">
                Start scraping products to see your history here
              </p>
            </div>
          )}

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="pt-4 border-t mt-4 space-y-3">
              <div className="text-sm text-muted-foreground text-center">
                Showing page {data.page} of {data.totalPages} ({data.total}{' '}
                total jobs)
              </div>
              <Pagination
                currentPage={data.page}
                totalPages={data.totalPages}
                onPageChange={setPage}
                className="justify-center"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
